import React from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import PrivateRoute from './components/common/PrivateRoute';
// Les écrans privés autonomes reçoivent la même charte sans changer leur protection.
import PrivateUIProvider from './components/common/PrivateUIProvider';
import ProtectedLoginRoute from './components/common/ProtectedLoginRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import PublicLayout from './components/layout/PublicLayout';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
// Pages publiques dédiées : la récupération crée une session qui doit rester sur le formulaire.
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import CommandTemplates from './components/templates/CommandTemplates';
import Dashboard from './pages/Dashboard';
import Conversations from './pages/Conversations';
import Products from './pages/Products';
import MetaAdsCampaigns from './pages/MetaAdsCampaigns';
import Bundles from './pages/Bundles';
import Discounts from './pages/Discounts';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import Notifications from './pages/Notifications';
import Orders from './pages/Orders';
import OrderDetails from './components/orders/OrderDetails';
import UnresponsiveCustomers from './pages/UnresponsiveCustomers';
import DeliveryPersons from './pages/DeliveryPersons';
import DeliveryPersonForm from './pages/DeliveryPersons/DeliveryPersonForm';
import OrderAssignment from './pages/OrderAssignment';
import Cashiers from './pages/Cashiers';
import Locations from './pages/Locations';
import Clients from './pages/Clients';
import Profile from './pages/Profile';
import NotFound from './pages/NotFound';
import Shops from './pages/ShopManagement';
import ProductFormPage from './pages/ProductFormPage';
import ProductReviewsPage from './pages/ProductReviewsPage';
import Expenses from './pages/Expenses';
import CommerceAssistant from './pages/CommerceAssistant';
import WhatsAppQueue from './pages/WhatsAppQueue';
import ShopSelectionGate from './components/shops/ShopSelectionGate';
import ShopTeamManagement from './pages/ShopTeamManagement';
import TeamInvitation from './pages/TeamInvitation';

// Nouvelles pages publiques
import LandingPage from './pages/LandingPage';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Features from './pages/Features';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

import PublicOrderView from './pages/PublicOrderView';
import DeliveryPersonPublicView from './pages/DeliveryPersonPublicView';
import DailyOrdersPublicView from './pages/DailyOrdersPublicView';
import PublicFeedbackView from './pages/PublicFeedbackView';
import OrderFeedbacks from './pages/OrderFeedbacks';
import DigitalProductPreorders from './pages/DigitalProductPreorders';
import { supabase } from './utils/supabase';

// Composant pour gérer la redirection intelligente de la route racine
const RootRoute = () => {
  const [session, setSession] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  /**
   * Restaure la session Supabase dès l'arrivée sur la racine et écoute ses
   * changements pour rediriger l'utilisateur connecté vers son espace.
   */
  React.useEffect(() => {
    let composantActif = true;

    /**
     * Lit la session enregistrée localement par Supabase avant l'affichage
     * de la page afin de différencier un visiteur d'un utilisateur connecté.
     */
    const restaurerSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (composantActif) {
          setSession(session);
        }
      } finally {
        if (composantActif) {
          setLoading(false);
        }
      }
    };

    restaurerSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nouvelleSession) => {
      if (composantActif) {
        setSession(nouvelleSession);
        setLoading(false);
      }
    });

    return () => {
      composantActif = false;
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Si connecté, rediriger vers le dashboard immédiatement
  if (session) {
    return <Navigate to="/app/dashboard" replace />;
  }

  // Si non connecté, afficher la landing page avec PublicLayout
  return (
    <PublicLayout>
      <LandingPage />
    </PublicLayout>
  );
};

const router = createBrowserRouter([
  // Pas de ProtectedLoginRoute : une session de récupération ne doit pas être redirigée au tableau de bord.
  { path: '/forgot-password', element: <PublicLayout><ForgotPasswordForm /></PublicLayout> },
  { path: '/reset-password', element: <PublicLayout><ResetPasswordForm /></PublicLayout> },
  // Route racine indépendante pour éviter le flash
  {
    path: '/',
    element: <RootRoute />,
  },
  // Routes publiques avec PublicLayout
  {
    path: '/public',
    element: <PublicLayout />,
    children: [
      {
        path: 'about',
        element: <About />,
      },
      {
        path: 'features',
        element: <Features />,
      },
      {
        path: 'contact',
        element: <Contact />,
      },
      {
        path: 'privacy',
        element: <Privacy />,
      },
      {
        path: 'terms',
        element: <Terms />,
      },
      {
        path: 'login',
        element: <ProtectedLoginRoute><LoginForm /></ProtectedLoginRoute>,
      },
      {
        path: 'register',
        element: <ProtectedLoginRoute><RegisterForm /></ProtectedLoginRoute>,
      },
    ],
  },
  // Routes publiques directes pour compatibilité
  {
    path: '/about',
    element: <PublicLayout><About /></PublicLayout>,
  },
  {
    path: '/features',
    element: <PublicLayout><Features /></PublicLayout>,
  },
  {
    path: '/contact',
    element: <PublicLayout><Contact /></PublicLayout>,
  },
  {
    path: '/privacy',
    element: <PublicLayout><Privacy /></PublicLayout>,
  },
  {
    path: '/terms',
    element: <PublicLayout><Terms /></PublicLayout>,
  },
  {
    path: '/login',
    element: <PublicLayout><ProtectedLoginRoute><LoginForm /></ProtectedLoginRoute></PublicLayout>,
  },
  {
    path: '/register',
    element: <PublicLayout><ProtectedLoginRoute><RegisterForm /></ProtectedLoginRoute></PublicLayout>,
  },
  // Page Pricing indépendante avec son propre header
  {
    path: '/pricing',
    // Même en-tête / pied de page que les autres pages publiques (l'ancien en-tête propre est supprimé)
    element: <PublicLayout><Pricing /></PublicLayout>,
  },
  // Page publique de visualisation de commande (accès direct par lien)
  {
    path: '/order-view/:id',
    element: <PublicOrderView />,
  },
  // Page publique de feedback/avis après commande (accès direct par lien)
  {
    path: '/feedback/:orderId',
    element: <PublicFeedbackView />,
  },
  // Page publique pour les livreurs - accès sans authentification via token sécurisé
  {
    path: '/delivery/my-orders/:token',
    element: <DeliveryPersonPublicView />,
  },
  // Invitation publique : la page invite à se connecter avant l'acceptation sécurisée.
  {
    path: '/team-invitations/:token',
    element: <TeamInvitation />,
  },
  // Page publique des commandes du jour d'un utilisateur - accès via token partageable
  {
    path: '/daily-orders/:token',
    element: <DailyOrdersPublicView />,
  },
  // Routes privées avec DashboardLayout
  {
    path: '/app/select-shop',
    element: <PrivateUIProvider><PrivateRoute skipPlanCheck><ShopSelectionGate /></PrivateRoute></PrivateUIProvider>,
  },
  // Gestion compte indépendante : utilisable sans boutique active.
  {
    path: '/account/shops',
    element: <PrivateUIProvider><PrivateRoute skipPlanCheck><Shops /></PrivateRoute></PrivateUIProvider>,
  },
  // L'équipe reste liée à une boutique précise, indépendamment de la boutique active.
  {
    path: '/account/shops/:shopId/team',
    element: <PrivateUIProvider><PrivateRoute skipPlanCheck><ShopTeamManagement /></PrivateRoute></PrivateUIProvider>,
  },
  {
    path: '/app',
    element: <DashboardLayout />,
    children: [
      {
        path: '',
        element: <PrivateRoute><Dashboard /></PrivateRoute>,
      },
      {
        path: 'dashboard',
        element: <PrivateRoute><Dashboard /></PrivateRoute>,
      },
      {
        path: 'orders',
        element: <PrivateRoute><Orders /></PrivateRoute>,
      },
      {
        path: 'assistant',
        element: <PrivateRoute><CommerceAssistant /></PrivateRoute>,
      },
      {
        path: 'whatsapp-queue',
        element: <PrivateRoute><WhatsAppQueue /></PrivateRoute>,
      },
      {
        path: 'orders/:id',
        element: <PrivateRoute><OrderDetails /></PrivateRoute>,
      },
      {
        path: 'products',
        element: <PrivateRoute><Products /></PrivateRoute>,
      },
      {
        path: 'bundles',
        element: <PrivateRoute><Bundles /></PrivateRoute>,
      },
      {
        path: 'discounts',
        element: <PrivateRoute><Discounts /></PrivateRoute>,
      },
      // Création de campagnes publicitaires Meta assistée par IA
      {
        path: 'meta-ads',
        element: <PrivateRoute><MetaAdsCampaigns /></PrivateRoute>,
      },
      {
        path: 'products/new',
        element: <PrivateRoute><ProductFormPage /></PrivateRoute>,
      },
      {
        path: 'products/:id/edit',
        element: <PrivateRoute><ProductFormPage /></PrivateRoute>,
      },
      {
        path: 'products/:productId/reviews',
        element: <PrivateRoute><ProductReviewsPage /></PrivateRoute>,
      },
      {
        path: 'settings',
        element: <PrivateRoute><Settings /></PrivateRoute>,
      },
      {
        path: 'analytics',
        element: <PrivateRoute><Analytics /></PrivateRoute>,
      },
      {
        path: 'notifications',
        element: <PrivateRoute><Notifications /></PrivateRoute>,
      },
      {
        path: 'unresponsive-customers',
        element: <PrivateRoute><UnresponsiveCustomers /></PrivateRoute>,
      },
      {
        path: 'templates',
        element: <PrivateRoute><CommandTemplates /></PrivateRoute>,
      },
      {
        path: 'delivery-persons',
        element: <PrivateRoute><DeliveryPersons /></PrivateRoute>,
      },
      {
        path: 'delivery-persons/:id',
        element: <PrivateRoute><DeliveryPersonForm /></PrivateRoute>,
      },
      {
        path: 'delivery-persons-orders',
        element: <PrivateRoute><OrderAssignment /></PrivateRoute>,
      },
      {
        path: 'cashiers',
        element: <PrivateRoute><Cashiers /></PrivateRoute>,
      },
      {
        path: 'locations',
        element: <PrivateRoute><Locations /></PrivateRoute>,
      },
      {
        path: 'shops',
        element: <PrivateRoute><Shops /></PrivateRoute>,
      },
      // Point d'entrée du menu dans le dashboard : la boutique active est résolue sans quitter le layout.
      {
        path: 'users',
        element: <PrivateRoute skipPlanCheck><ShopTeamManagement /></PrivateRoute>,
      },
      {
        path: 'clients',
        element: <PrivateRoute><Clients /></PrivateRoute>,
      },
      {
        path: 'feedbacks',
        element: <PrivateRoute><OrderFeedbacks /></PrivateRoute>,
      },
      {
        path: 'digital-products',
        element: <PrivateRoute><DigitalProductPreorders /></PrivateRoute>,
      },
      {
        path: 'expenses',
        element: <PrivateRoute><Expenses /></PrivateRoute>,
      },
      {
        path: 'profile',
        element: <PrivateRoute><Profile /></PrivateRoute>,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  // Redirection des anciennes routes vers les nouvelles
  {
    path: '/dashboard',
    element: <PrivateRoute redirectTo="/app/dashboard"><></></PrivateRoute>,
  },
  {
    path: '/orders',
    element: <PrivateRoute redirectTo="/app/orders"><></></PrivateRoute>,
  },
  {
    path: '/products',
    element: <PrivateRoute redirectTo="/app/products"><></></PrivateRoute>,
  },
  {
    path: '/bundles',
    element: <PrivateRoute redirectTo="/app/bundles"><></></PrivateRoute>,
  },
  {
    path: '/discounts',
    element: <PrivateRoute redirectTo="/app/discounts"><></></PrivateRoute>,
  },
  {
    path: '/settings',
    element: <PrivateRoute redirectTo="/app/settings"><></></PrivateRoute>,
  },
  {
    path: '/analytics',
    element: <PrivateRoute redirectTo="/app/analytics"><></></PrivateRoute>,
  },
  {
    path: '/notifications',
    element: <PrivateRoute redirectTo="/app/notifications"><></></PrivateRoute>,
  },
  {
    path: '/unresponsive-customers',
    element: <PrivateRoute redirectTo="/app/unresponsive-customers"><></></PrivateRoute>,
  },
  {
    path: '/templates',
    element: <PrivateRoute redirectTo="/app/templates"><></></PrivateRoute>,
  },
  {
    path: '/delivery-persons',
    element: <PrivateRoute redirectTo="/app/delivery-persons"><></></PrivateRoute>,
  },
  {
    path: '/delivery-persons-orders',
    element: <PrivateRoute redirectTo="/app/delivery-persons-orders"><></></PrivateRoute>,
  },
  {
    path: '/cashiers',
    element: <PrivateRoute redirectTo="/app/cashiers"><></></PrivateRoute>,
  },
  {
    path: '/shops',
    element: <PrivateRoute redirectTo="/app/shops"><></></PrivateRoute>,
  },
  {
    path: '/clients',
    element: <PrivateRoute redirectTo="/app/clients"><></></PrivateRoute>,
  },
  {
    path: '/profile',
    element: <PrivateRoute redirectTo="/app/profile"><></></PrivateRoute>,
  },
  // Gestion des 404
  {
    path: '*',
    element: <NotFound />,
  },
]);

export default router; 
